import { lazy, Suspense, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  ACHIEVEMENT_STORAGE_KEY, REWARDS, REWARD_IDS,
  buildPendingMatch, clearGame, createInitialState, createPromotionMovement, gameReducer, generateScoutOffer,
  bossReactionOdds, canAcquireReward, canNavigateToNode, canUseCard,
  discoverRewards, emptyAchievementProfile, generateStageMap, getCareerFinale, getCareerMoveDecisionEffects, getCareerMoveOutcome, getCareerMoveRisk, getCareerStage, getClubBossRivals, getDerbyRivals, getEventChoiceChanges, getInternationalRivals, getInventoryCount, getLeagueBlockFixtureCount, getLeagueRivals, getLeagueSchedule, getRankedTransferDecisions, getRewardDecisionEffects, getSeniorClubForReserve, getStageBoss, getStateAwareEventChoices, getTransferOffers, getValueChangeTone, getWorldCup2026GroupRivals, isBossSeriesFinished, isWorldCupElimination, loadAchievementProfile, loadGame, newlyUnlockedAchievements, overallGainForResult, parseAchievementProfile, parseGameState, restRecovery, rewardIdsDiscoveredInState, saveAchievementProfile, saveGame, unlockAchievements,
  getFootballEventContext, getWorldCup2026RoutePhaseTitle, pickContextualEvent,
  type AchievementProfile, type CardRewardId, type DecisionChoice,
  type EncounterKind, type GameAction, type GameStateV2, type MatchOutcome,
  type MapNode, type Player, type RewardId,
} from './game'
export { EVENTS } from './game'
import { MatchTicker, isNarrowLoss, type MatchSummary } from './match'
import { DecisionDialog, withPlayerStats, type DecisionDialogModel } from './components/DecisionDialog'
import { MatchPrepDialog } from './components/MatchPrepDialog'
import { SetupScreen } from './components/SetupScreen'
import { SeasonMap } from './components/SeasonMap'
import { Sidebar, SidebarMenuButton } from './components/Sidebar'
import { TopbarActions } from './components/TopbarActions'
import { ChangelogDialog } from './components/ChangelogDialog'
import { AchievementDialog } from './components/AchievementDialog'
import { CareerReportDialog } from './components/CareerReportDialog'
import { BossSeriesResultDialog } from './components/BossSeriesResultDialog'
import { AccountDialog } from './components/AccountDialog'
import { AppLoadingScreen } from './components/AppLoadingScreen'
import { FirstVisitTutorial, hasSeenFirstVisitTutorial, markFirstVisitTutorialSeen } from './components/FirstVisitTutorial'
import { useModalFocus } from './components/useModalFocus'
import { loadCloudProgress, saveCloudProgress } from './auth/progress'
import { isCloudSyncConfigured, supabase } from './auth/supabase'
import { useAuthSession } from './auth/useAuthSession'

const GlossaryDialog = lazy(() => import('./components/GlossaryDialog').then((module) => ({ default: module.GlossaryDialog })))

type NullableGameAction = GameAction | { type: 'run/cleared' }

function nullableReducer(state: GameStateV2 | null, action: NullableGameAction): GameStateV2 | null {
  if (action.type === 'run/cleared') return null
  if (!state) return action.type === 'run/replaced' ? action.state : null
  return gameReducer(state, action)
}

function restoreState(): GameStateV2 | null {
  const restored = loadGame(window.localStorage)
  return restored
}

interface MatchRun {
  id: string
  encounter: EncounterKind
  opponent: string
  outcome: MatchOutcome
  probability: number
  assistBonus: number
  activeCardId: CardRewardId | null
}

interface PrepEncounter {
  kind: EncounterKind
  opponent: string
}

function pickEncounterOpponent(state: GameStateV2, encounter: EncounterKind): string {
  const international = state.season === 5 && state.finaleKind === 'world-cup' && (encounter === 'boss' || (encounter === 'match' && state.map[state.week]?.some((node) => node.type === 'match') === true))
  const leagueCandidates = getLeagueRivals(state.club, state.leagueMovements).map((club) => club.name)
  const bossCandidates = getClubBossRivals(state.season, state.club, state.boss?.round ?? 0, state.leagueMovements, state.finaleKind).map((club) => club.name)
  const derbyCandidates = getDerbyRivals(state.club, state.leagueMovements).map((club) => club.name)
  const preferredCandidates = encounter === 'boss'
    ? bossCandidates
    : encounter === 'derby' && derbyCandidates.length ? derbyCandidates : leagueCandidates
  const unplayedCandidates = preferredCandidates.filter((club) => !state.leagueOpponentsPlayed.includes(club))
  const candidates = international
    ? [...getInternationalRivals(state.player.country)]
    : unplayedCandidates.length ? unplayedCandidates : preferredCandidates
  if (!candidates.length) return ''
  const round = encounter === 'boss' ? state.boss?.round ?? 0 : state.week
  const fingerprint = `${state.club}-${state.player.name}-${state.season}-${state.week}-${encounter}-${round}`
  const hash = [...fingerprint].reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 0)
  return candidates[hash % candidates.length]!
}

function rollMatchOutcome(winProbability: number): MatchOutcome {
  return Math.random() < winProbability ? 'win' : 'loss'
}

interface Notice {
  title: string
  text: string
  label: string
  action: () => void
  secondaryLabel?: string
  secondaryAction?: () => void
}

const REST_CHOICES: DecisionChoice[] = [
  { id: 'rest-recovery', title: 'Recuperación física', summary: 'Trabajo suave, fisioterapia y descanso completo.', art: 'rest-recovery', badge: 'Físico', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+30', tone: 'gain' }] },
  { id: 'rest-morale', title: 'Desconectar con el equipo', summary: 'Una tarde fuera del campo para recomponer el vestuario.', art: 'rest-team', badge: 'Moral', risk: 'low', effects: [{ metric: 'morale', label: 'Moral', value: '+24', tone: 'gain' }] },
  { id: 'rest-balanced', title: 'Descanso equilibrado', summary: 'Recuperas cuerpo y cabeza sin especializar la sesión.', art: 'rest-team', badge: 'Equilibrado', risk: 'low', effects: [{ metric: 'physical', label: 'Físico', value: '+12', tone: 'gain' }, { metric: 'morale', label: 'Moral', value: '+8', tone: 'gain' }] },
]

export default function App() {
  const [isBooting, setIsBooting] = useState(true)
  const [state, dispatch] = useReducer(nullableReducer, null, restoreState)
  const [achievementProfile, setAchievementProfile] = useState<AchievementProfile>(() => loadAchievementProfile(localStorage))
  const { user, ready: authReady, passwordRecovery, completePasswordRecovery } = useAuthSession()
  const [hydratedUserId, setHydratedUserId] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<'local' | 'loading' | 'saving' | 'synced' | 'error'>('local')
  const [theme, setTheme] = useState<'auto' | 'light' | 'dark'>(() => (localStorage.getItem('footballRoguelikeTheme') as 'auto' | 'light' | 'dark') || 'auto')
  const [dialog, setDialog] = useState<DecisionDialogModel | null>(null)
  const actionsRef = useRef<Record<string, (outcomeIndex?: number) => void>>({})
  const bossReactionResultRef = useRef<{ state: GameStateV2; note: string } | null>(null)
  const matchResultDialogRef = useRef<HTMLElement>(null)
  const noticeDialogRef = useRef<HTMLElement>(null)
  const [prepEncounter, setPrepEncounter] = useState<PrepEncounter | null>(null)
  const [matchRun, setMatchRun] = useState<MatchRun | null>(null)
  const [bossSeriesResult, setBossSeriesResult] = useState<GameStateV2 | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [showChangelog, setShowChangelog] = useState(false)
  const [showAchievements, setShowAchievements] = useState(false)
  const [showGlossary, setShowGlossary] = useState(false)
  const [showAccount, setShowAccount] = useState(() => window.location.hash === '#account')
  const [showFirstVisitTutorial, setShowFirstVisitTutorial] = useState(() => !hasSeenFirstVisitTutorial(localStorage))
  const [sidebarOpen, setSidebarOpen] = useState(() => !window.matchMedia('(max-width: 520px)').matches)
  const waitingForAccountProgress = !authReady || Boolean(user && hydratedUserId !== user.id && syncStatus !== 'error')
  const shouldShowFirstVisitTutorial = Boolean(state && showFirstVisitTutorial)

  useEffect(() => {
    const timer = window.setTimeout(() => setIsBooting(false), 350)
    return () => window.clearTimeout(timer)
  }, [])
  useEffect(() => {
    if (!isBooting && !waitingForAccountProgress && shouldShowFirstVisitTutorial) markFirstVisitTutorialSeen(localStorage)
  }, [isBooting, shouldShowFirstVisitTutorial, waitingForAccountProgress])
  useEffect(() => {
    if (passwordRecovery) setShowAccount(true)
  }, [passwordRecovery])

  useModalFocus(Boolean(state && matchRun && state.pendingMatch), matchResultDialogRef)
  useModalFocus(Boolean(notice), noticeDialogRef, {
    initialFocusSelector: notice?.secondaryAction ? '[data-modal-cancel]' : undefined,
    onEscape: notice?.secondaryAction,
  })

  useEffect(() => {
    const system = matchMedia('(prefers-color-scheme: light)')
    const apply = () => document.documentElement.dataset.theme = theme === 'auto' ? (system.matches ? 'light' : 'dark') : theme
    apply(); localStorage.setItem('footballRoguelikeTheme', theme)
    system.addEventListener('change', apply)
    return () => system.removeEventListener('change', apply)
  }, [theme])

  useEffect(() => { if (state) saveGame(localStorage, state) }, [state])
  useEffect(() => { saveAchievementProfile(localStorage, achievementProfile) }, [achievementProfile])
  useEffect(() => {
    if (!user || !isCloudSyncConfigured) {
      setHydratedUserId(null)
      setSyncStatus('local')
      return
    }

    let active = true
    setHydratedUserId(null)
    setSyncStatus('loading')

    void loadCloudProgress(user.id).then(async (cloud) => {
      if (!active) return
      if (cloud) {
        const restoredState = cloud.gameState === null ? null : parseGameState(cloud.gameState)
        if (cloud.gameState !== null && !restoredState) throw new Error('La copia remota de la partida no es válida.')
        const restoredAchievements = parseAchievementProfile(cloud.achievements)
        dispatch(restoredState ? { type: 'run/replaced', state: restoredState } : { type: 'run/cleared' })
        if (restoredState) saveGame(localStorage, restoredState)
        else clearGame(localStorage)
        setAchievementProfile(restoredAchievements)
        saveAchievementProfile(localStorage, restoredAchievements)
      } else {
        const emptyAccountAchievements = emptyAchievementProfile()
        await saveCloudProgress(user.id, null, emptyAccountAchievements)
        if (!active) return
        clearGame(localStorage)
        dispatch({ type: 'run/cleared' })
        setAchievementProfile(emptyAccountAchievements)
        saveAchievementProfile(localStorage, emptyAccountAchievements)
      }
      if (!active) return
      setHydratedUserId(user.id)
      setSyncStatus('synced')
    }).catch(() => {
      if (!active) return
      setSyncStatus('error')
    })

    return () => { active = false }
  }, [user?.id])

  useEffect(() => {
    if (!user || hydratedUserId !== user.id) return
    setSyncStatus('saving')
    const timer = window.setTimeout(() => {
      void saveCloudProgress(user.id, state, achievementProfile)
        .then(() => setSyncStatus('synced'))
        .catch(() => setSyncStatus('error'))
    }, 800)
    return () => window.clearTimeout(timer)
  }, [achievementProfile, hydratedUserId, state, user?.id])
  useEffect(() => {
    if (!state) return
    const unlocked = newlyUnlockedAchievements(state, achievementProfile)
    if (!unlocked.length) return
    setAchievementProfile((current) => unlockAchievements(current, unlocked))
  }, [state, achievementProfile])
  useEffect(() => {
    if (!state) return
    const visibleRewardIds = rewardIdsDiscoveredInState(state)
    if (!visibleRewardIds.length) return
    setAchievementProfile((current) => discoverRewards(current, visibleRewardIds))
  }, [state?.inventory, state?.pendingScoutOffer])
  useEffect(() => {
    if (!state?.pendingMatch || matchRun) return
    const pending = state.pendingMatch
    setMatchRun({
      id: pending.matchId ?? `legacy-${state.season}-${state.week}`,
      encounter: pending.encounterKind,
      opponent: pending.opponent ?? pickEncounterOpponent(state, pending.encounterKind),
      outcome: pending.outcome ?? rollMatchOutcome(pending.winProbability),
      probability: pending.winProbability,
      assistBonus: pending.assistChanceBonus,
      activeCardId: pending.activeCardId,
    })
  }, [matchRun, state])
  useEffect(() => {
    if (!state || state.finished || !isBossSeriesFinished(state.season, state.club, state.boss, state.leagueMovements, state.finaleKind)) return
    if (dialog || matchRun || (prepEncounter && prepEncounter.kind !== 'boss')) return
    if (prepEncounter?.kind === 'boss') setPrepEncounter(null)
    if (!bossSeriesResult) setBossSeriesResult(state)
  }, [bossSeriesResult, dialog, matchRun, prepEncounter, state])

  const openDecision = (model: DecisionDialogModel, actions: Record<string, (outcomeIndex?: number) => void>) => {
    actionsRef.current = actions
    setDialog(state ? withPlayerStats(model, state) : model)
  }

  const resolveChoice = async (choice: DecisionChoice, outcomeIndex?: number) => {
    actionsRef.current[choice.id]?.(outcomeIndex)
    setDialog(null)
  }

  const start = (player: Player, club: string) => dispatch({ type: 'run/replaced', state: createInitialState(player, generateStageMap(1), club) })

  const resolveSimple = (choice: DecisionChoice, changes: { physical?: number; morale?: number; ovr?: number; reputation?: number }, message: string) => {
    dispatch({ type: 'decision/resolved', ...changes, message })
  }

  const openRest = () => {
    if (!state) return
    const physicalRest = restRecovery(state, 'physical')
    const moraleRest = restRecovery(state, 'morale')
    const balancedRest = restRecovery(state, 'balanced')
    const choices: DecisionChoice[] = [
      { ...REST_CHOICES[0], effects: [{ metric: 'physical', label: 'Físico', value: `+${physicalRest.physical}`, tone: 'gain' }] },
      { ...REST_CHOICES[1], effects: [{ metric: 'morale', label: 'Moral', value: `+${moraleRest.morale}`, tone: 'gain' }] },
      { ...REST_CHOICES[2], effects: [{ metric: 'physical', label: 'Físico', value: `+${balancedRest.physical}`, tone: 'gain' }, { metric: 'morale', label: 'Moral', value: `+${balancedRest.morale}`, tone: 'gain' }] },
    ]
    openDecision(
      { eyebrow: 'Descanso', title: 'Un respiro en la etapa', description: 'Tus reliquias de Recuperación y Vestuario modifican estas opciones automáticamente.', icon: 'mdi:grass', stats: state, choices },
      {
        'rest-recovery': () => resolveSimple(choices[0], physicalRest, 'La build convierte la sesión de recuperación en energía para el siguiente tramo.'),
        'rest-morale': () => resolveSimple(choices[1], moraleRest, 'La desconexión recompone el ánimo del vestuario.'),
        'rest-balanced': () => resolveSimple(choices[2], balancedRest, 'El descanso equilibrado recupera cuerpo y cabeza.'),
      },
    )
  }

  const openEvent = () => {
    if (!state) return
    const sourceEvent = pickContextualEvent(getFootballEventContext(state.season, state.club, state.leagueMovements, state.finaleKind === 'world-cup' ? state.player.country : undefined))
    const requirements: Partial<Record<string, RewardId>> = {
      'conditioning-biometric': 'pulsera-biometrica',
      'locker-bracelet': 'brazalete',
      'physio-camilla': 'camilla-portatil',
    }
    const event = {
      ...sourceEvent,
      choices: getStateAwareEventChoices(sourceEvent, state).map((choice) => {
        const required = requirements[choice.id]
        return required && getInventoryCount(state.inventory, required) < 1
          ? { ...choice, disabled: true, summary: `${choice.summary} Requiere ${REWARDS[required].name}.` }
          : choice
      }),
    }
    const choice = (id: string) => event.choices.find((item) => item.id === id)!
    const changes = (id: string) => getEventChoiceChanges(id)!
    const resolveWithObject = (choiceId: string, rewardId: CardRewardId, changes: { physical?: number; morale?: number; ovr?: number }, message: string) => {
      dispatch({ type: 'reward/acquired', rewardId })
      resolveSimple(choice(choiceId), changes, message)
    }
    const actions: Record<string, (outcomeIndex?: number) => void> = {
      'training-extra': () => resolveSimple(choice('training-extra'), changes('training-extra'), 'La sesión extra acelera tu desarrollo.'),
      'training-rest': () => resolveSimple(choice('training-rest'), changes('training-rest'), 'Recuperas cuerpo y cabeza, pero el cuerpo técnico percibe menos ambición.'),
      'coach-demand': (outcomeIndex) => { const ok = outcomeIndex === undefined ? Math.random() < .5 : outcomeIndex === 0; resolveSimple(event.choices[0], ok ? { ovr: 1, morale: 12 } : { morale: -10 }, ok ? 'Tu ambición convence al entrenador.' : 'El entrenador cree que te has precipitado.') },
      'coach-accept': () => resolveSimple(choice('coach-accept'), changes('coach-accept'), 'Aceptas el plan y respondes con trabajo táctico.'),
      'injury-stop': () => resolveSimple(choice('injury-stop'), changes('injury-stop'), 'Proteges el músculo, aunque cedes terreno en la jerarquía.'),
      'injury-push': (outcomeIndex) => { const ok = outcomeIndex === undefined ? Math.random() < .6 : outcomeIndex === 0; resolveSimple(event.choices[1], ok ? { ovr: 1 } : { physical: -28 }, ok ? 'Superas la sesión sin problemas.' : 'La sobrecarga empeora.') },
      'press-speak': () => resolveSimple(choice('press-speak'), changes('press-speak'), 'La entrevista conecta con la afición.'),
      'press-focus': () => resolveSimple(choice('press-focus'), changes('press-focus'), 'El trabajo táctico mejora tu confianza a costa de energía.'),
      'academy-mentor': () => resolveSimple(choice('academy-mentor'), changes('academy-mentor'), 'La cantera te recuerda por qué empezaste.'),
      'academy-drill': () => resolveSimple(choice('academy-drill'), changes('academy-drill'), 'La intensidad de los juveniles te exige de verdad.'),
      'conditioning-load': () => resolveSimple(choice('conditioning-load'), changes('conditioning-load'), 'La carga desarrolla tu juego a costa de energía.'),
      'conditioning-biometric': () => resolveSimple(choice('conditioning-biometric'), changes('conditioning-biometric'), 'Los datos de la pulsera optimizan la sesión.'),
      'conditioning-recover': () => resolveSimple(choice('conditioning-recover'), changes('conditioning-recover'), 'La descarga recupera las piernas, pero deja la carga incompleta.'),
      'locker-mediate': () => resolveSimple(choice('locker-mediate'), changes('locker-mediate'), 'Tu intervención recompone el vestuario.'),
      'locker-bracelet': () => resolveWithObject('locker-bracelet', 'charla-capitan', changes('locker-bracelet'), 'El Brazalete transforma el conflicto en liderazgo.'),
      'physio-treatment': () => resolveSimple(choice('physio-treatment'), changes('physio-treatment'), 'El tratamiento recupera el cuerpo a costa de ánimo.'),
      'physio-camilla': () => resolveWithObject('physio-camilla', 'gel-energetico', changes('physio-camilla'), 'La Camilla portátil completa un protocolo avanzado y exigente.'),
    }
    openDecision({ eyebrow: 'Acontecimiento', title: event.title, description: event.description, icon: event.icon, choices: event.choices }, actions)
  }

  const openScout = (offer?: readonly RewardId[]) => {
    if (!state) return
    const candidates = [...(offer?.length ? offer : state.pendingScoutOffer?.length ? state.pendingScoutOffer : generateScoutOffer(state.inventory, state.player.position))]
    const choices = candidates.map((id): DecisionChoice => {
      const reward = REWARDS[id]
      return { id: `reward:${id}`, title: reward.name, summary: reward.description, art: reward.art, badge: `${reward.kindLabel} · ${reward.family}`, risk: 'low', effects: getRewardDecisionEffects(reward) }
    })
    const actions = Object.fromEntries(candidates.map((id) => [`reward:${id}`, () => {
      dispatch({ type: 'reward/acquired', rewardId: id })
      dispatch({ type: 'node/completed', message: `El ojeador entrega ${REWARDS[id].name}: ${REWARDS[id].description}` })
    }]))
    openDecision({ eyebrow: 'Informe del ojeador', title: 'Elige una mejora para tu build', description: 'Los consumibles se gastan al usarlos. Las reliquias permanecen activas y sus combinaciones pueden formar sinergias.', icon: 'mdi:binoculars', stats: state, choices }, actions)
  }

  const openNode = (node: MapNode) => {
    if (!state || !canNavigateToNode(state, node)) return
    const scoutOffer = node.type === 'scout'
      ? state.activeNodeId === node.id && state.pendingScoutOffer?.length
        ? state.pendingScoutOffer
        : generateScoutOffer(state.inventory, state.player.position)
      : undefined
    dispatch({ type: 'node/opened', nodeId: node.id, scoutOffer })
    if (node.type === 'rest') openRest()
    else if (node.type === 'event') openEvent()
    else if (node.type === 'scout') openScout(scoutOffer)
    else {
      const kind = node.type === 'derby' ? 'derby' : 'match'
      setPrepEncounter({ kind, opponent: pickEncounterOpponent(state, kind) })
    }
  }

  const prepareMatch = (cardId: CardRewardId | null) => {
    if (!state || !prepEncounter) return
    if (prepEncounter.kind === 'boss' && isBossSeriesFinished(state.season, state.club, state.boss, state.leagueMovements, state.finaleKind)) {
      setPrepEncounter(null)
      setBossSeriesResult(state)
      return
    }
    const pending = buildPendingMatch(state, prepEncounter.kind, cardId)
    const outcome = rollMatchOutcome(pending.winProbability)
    const matchId = `${state.season}-${state.week}-${prepEncounter.kind}-${Math.floor(Math.random() * 0xffffffff).toString(36)}`
    dispatch({ type: 'match/cardSelected', rewardId: cardId })
    dispatch({ type: 'match/confirmed', encounterKind: prepEncounter.kind, outcome, opponent: prepEncounter.opponent, matchId })
    setMatchRun({ id: matchId, encounter: prepEncounter.kind, opponent: prepEncounter.opponent, outcome, probability: pending.winProbability, assistBonus: pending.assistChanceBonus, activeCardId: cardId })
    setPrepEncounter(null)
  }

  const startBoss = () => {
    if (!state || state.week < state.map.length) return
    if (isBossSeriesFinished(state.season, state.club, state.boss, state.leagueMovements, state.finaleKind)) {
      setBossSeriesResult(state)
      return
    }
    if (!state.boss) dispatch({ type: 'boss/started' })
    setPrepEncounter({ kind: 'boss', opponent: pickEncounterOpponent(state, 'boss') })
  }

  const showContract = (resolved: GameStateV2) => {
    const completedStage = getCareerStage(resolved.season, resolved.finaleKind)
    const completedBoss = getStageBoss(resolved.season, resolved.club, resolved.leagueMovements, resolved.finaleKind)
    const promotedClub = resolved.season === 1 ? getSeniorClubForReserve(resolved.club) : undefined
    const nextFinale = resolved.season === 4 ? getCareerFinale(resolved) : resolved.finaleKind ?? 'world-cup'
    const nextMap = () => generateStageMap(resolved.season + 1, Math.random, nextFinale)
    const transferOffers = getTransferOffers(resolved.player.country, resolved.season, resolved.club, 2, Math.random, resolved.leagueMovements)
    const rankedTransferOffers = getRankedTransferDecisions(resolved, transferOffers.map((club) => club.name))
      .map(({ club: clubName, risk }) => ({ club: transferOffers.find((club) => club.name === clubName)!, risk }))
    const continuityClub = promotedClub?.name ?? resolved.club
    const continuityMove = promotedClub ? 'reserve-promotion' as const : 'renewal' as const
    const stayOutcome = getCareerMoveOutcome(resolved, continuityClub, continuityMove)
    const stayChoice: DecisionChoice = promotedClub
      ? { id: 'contract-stay', title: `Mantenerse y subir al primer equipo de ${promotedClub.name}`, summary: 'Sigues dentro del club: dejas el filial, debutas arriba y tendrás que ganarte cada minuto.', art: 'contract-stay', clubName: promotedClub.name, badge: 'Continuidad · Primer equipo', risk: getCareerMoveRisk(continuityMove), effects: [
          { metric: 'reputation', label: 'Confianza inicial', value: `${stayOutcome.clubReputation}/100`, tone: 'warning', when: `${Math.round(stayOutcome.appearanceProbability * 100)}% de jugar cada partido` },
          { metric: 'morale', label: 'Moral inicial', value: `${stayOutcome.morale}/100`, tone: 'gain', when: 'La promoción refuerza tu confianza' },
          { metric: 'persistent', label: 'Lealtad', value: 'Se conserva', tone: 'gain', when: 'Filial y primer equipo cuentan como el mismo club' },
        ] }
      : { id: 'contract-stay', title: `Seguir en ${resolved.club}`, summary: 'Apuestas por la continuidad, el sistema y la conexión con la afición.', art: 'contract-stay', clubName: resolved.club, badge: 'Renovación', risk: getCareerMoveRisk(continuityMove), effects: [
          ...(stayOutcome.ovr > resolved.ovr ? [{ metric: 'rating' as const, label: 'OVR', value: `+${stayOutcome.ovr - resolved.ovr}`, tone: 'gain' as const, when: 'Tu prestigio convierte la continuidad en progreso' }] : []),
          { metric: 'reputation', label: 'Prestigio', value: `${stayOutcome.clubReputation}/100`, tone: 'gain', when: `${Math.round(stayOutcome.appearanceProbability * 100)}% de jugar cada partido` },
          { metric: 'morale', label: 'Moral inicial', value: `${stayOutcome.morale}/100`, tone: 'gain', when: 'Empiezas la etapa respaldado' },
          { metric: 'persistent', label: 'Lealtad', value: 'Se conserva', tone: 'gain', when: 'Mantienes vivos los retos de un solo club' },
        ] }
    const choices: DecisionChoice[] = [
      stayChoice,
      ...rankedTransferOffers.map(({ club, risk }, index): DecisionChoice => {
        const outcome = getCareerMoveOutcome(resolved, club.name, 'transfer')
        const competitiveDrop = Math.max(0, outcome.currentCompetitiveLevel - outcome.destinationCompetitiveLevel)
        const competitiveDirection = outcome.destinationCompetitiveLevel > outcome.currentCompetitiveLevel
          ? `Subes el techo deportivo en ${club.league}`
          : outcome.destinationCompetitiveLevel < outcome.currentCompetitiveLevel
            ? `${club.league} supone un escalón competitivo menor`
            : `Cambias de contexto sin elevar el nivel competitivo`
        return { id: `contract-transfer-${index}`, title: `Fichar por ${club.name}`, summary: `${competitiveDirection}, pero debes reconstruir tu estatus.`, art: 'contract-transfer', clubName: club.name, badge: `Traspaso · ${club.country}`, risk, effects: [
          ...getCareerMoveDecisionEffects(resolved.ovr, outcome),
          { metric: 'reputation', label: 'Prestigio', value: `${outcome.clubReputation}/100`, tone: getValueChangeTone(resolved.clubReputation, outcome.clubReputation), when: `${Math.round(outcome.appearanceProbability * 100)}% de jugar cada partido` },
          { metric: 'morale', label: 'Moral inicial', value: `${outcome.morale}/100`, tone: getValueChangeTone(resolved.morale, outcome.morale), when: competitiveDrop > 0 ? `Menos presión al bajar ${competitiveDrop} ${competitiveDrop === 1 ? 'nivel' : 'niveles'}` : 'La adaptación añade presión' },
          { metric: 'persistent', label: 'Lealtad', value: 'Se rompe', tone: 'cost', when: 'Renuncias a los retos de un solo club' },
        ] }
      }),
    ]
    const contractActions = Object.fromEntries(rankedTransferOffers.map(({ club }, index) => [`contract-transfer-${index}`, () => dispatch({ type: 'season/advanced', club: club.name, map: nextMap(), moveKind: 'transfer' })]))
    openDecision({ eyebrow: `Cierre de etapa ${resolved.season} · ${completedStage.name}`, title: promotedClub ? 'El primer equipo o un nuevo destino' : 'El siguiente capítulo', description: `${completedBoss.success} Compara nivel, minutos, moral y lealtad antes de decidir.`, icon: promotedClub ? 'mdi:account-arrow-up' : 'mdi:file-sign', stats: resolved, choices }, {
      'contract-stay': () => dispatch({ type: 'season/advanced', club: continuityClub, map: nextMap(), moveKind: continuityMove }),
      ...contractActions,
    })
  }

  const finishBoss = (resolved: GameStateV2) => {
    const wins = resolved.boss?.wins ?? 0
    const completedStage = getCareerStage(resolved.season, resolved.finaleKind)
    const completedBoss = getStageBoss(resolved.season, resolved.club, resolved.leagueMovements, resolved.finaleKind)
    if (wins < completedBoss.requiredWins) {
      const ended = gameReducer(resolved, { type: 'run/finished', message: `${completedBoss.failure} Has logrado ${wins} victorias de las ${completedBoss.requiredWins} necesarias.` })
      dispatch({ type: 'run/replaced', state: ended })
      return
    }
    let advancedState = resolved
    if (resolved.season === 3 && completedBoss.name === 'Playoff de ascenso') {
      const movement = createPromotionMovement(resolved.club, resolved.leagueMovements)
      if (movement) {
        advancedState = gameReducer(resolved, { type: 'league/promoted', movement })
        dispatch({ type: 'run/replaced', state: advancedState })
      }
    }
    if (advancedState.season >= 5) {
      const completed = gameReducer(advancedState, { type: 'run/finished', completed: true, message: completedBoss.success })
      dispatch({ type: 'run/replaced', state: completed })
    } else showContract(advancedState)
  }

  const continueAfterBossSeries = () => {
    if (!bossSeriesResult) return
    const resolved = bossSeriesResult
    setBossSeriesResult(null)
    finishBoss(resolved)
  }

  const finalizeMatch = (summary: MatchSummary, baseState: GameStateV2, outcome: MatchOutcome = summary.outcome, resolutionNote = '') => {
    if (!matchRun) return
    const isInternationalFixture = baseState.season === 5 && baseState.finaleKind === 'world-cup'
      && (matchRun.encounter === 'boss' || (matchRun.encounter === 'match' && baseState.map[baseState.week]?.some((node) => node.type === 'match') === true))
    const isWorldCupGroup = isInternationalFixture && baseState.week === 0 && matchRun.encounter === 'match'
    const isLeagueBlock = matchRun.encounter === 'match' && !isInternationalFixture
    const isMatchBlock = isLeagueBlock || isWorldCupGroup
    const outcomeLabel = isWorldCupGroup ? outcome === 'win' ? 'Clasificado' : 'Eliminado' : isLeagueBlock ? 'Tramo completado' : outcome === 'win' ? 'Victoria' : 'Derrota'
    const ovrGain = isLeagueBlock ? (summary.blockOvrGain ?? 0) : overallGainForResult(matchRun.encounter, outcome)
    const reputationGain = isLeagueBlock ? (summary.reputationGain ?? 0) : matchRun.encounter === 'derby' ? (outcome === 'win' ? 12 : -4) : 0
    const itemText = matchRun.activeCardId ? ` Se consumió ${REWARDS[matchRun.activeCardId].name}.` : ''
    const activeRelics = REWARD_IDS.filter((id) => REWARDS[id].kind === 'relic' && (baseState.inventory[id]?.count ?? 0) > 0)
    const relicText = activeRelics.slice(0, 3).map((id) => `${REWARDS[id].name} ×${baseState.inventory[id]?.count}`).join(', ')
    const appliedOvrGain = Math.min(ovrGain, Math.max(0, 99 - baseState.ovr))
    const progressionText = matchRun.encounter === 'boss' ? '' : matchRun.encounter === 'match' ? ` El bloque aporta +${appliedOvrGain} OVR.` : ` +${appliedOvrGain} OVR.`
    const scoreText = isWorldCupGroup ? `: ${summary.blockWins ?? 0} de ${summary.fixturesPlayed ?? 3} victorias en la fase de grupos.` : isLeagueBlock ? `: ${summary.blockPoints ?? 0} de ${(summary.fixturesPlayed ?? 3) * 3} puntos de liga (${summary.blockWins ?? 0}V-${summary.blockDraws ?? 0}E-${(summary.fixturesPlayed ?? 0) - (summary.blockWins ?? 0) - (summary.blockDraws ?? 0)}D); jugaste ${summary.blockAppearances ?? 0} partidos, con ${summary.playerGoals} goles y ${summary.playerAssists} asistencias.` : resolutionNote ? ` ${resolutionNote}` : ` ${summary.homeGoals}-${summary.awayGoals}.`
    const reputationText = reputationGain !== 0 ? ` Prestigio en el club ${reputationGain >= 0 ? '+' : ''}${reputationGain}.` : ''
    const outcomeText = `${outcomeLabel}${scoreText}${progressionText}${reputationText}${itemText}${relicText ? ` Build activa: ${relicText}${activeRelics.length > 3 ? ` y ${activeRelics.length - 3} más` : ''}.` : ''}`
    if (matchRun.encounter === 'boss') {
      const resolved = gameReducer(baseState, { type: 'boss/roundResolved', won: outcome === 'win', goals: summary.playerGoals, assists: summary.playerAssists })
      const logged = gameReducer(resolved, { type: 'log/added', message: outcomeText })
      dispatch({ type: 'run/replaced', state: logged })
      setMatchRun(null)
      if (isBossSeriesFinished(logged.season, logged.club, logged.boss, logged.leagueMovements, logged.finaleKind)) setBossSeriesResult(logged)
      else setPrepEncounter({ kind: 'boss', opponent: pickEncounterOpponent(logged, 'boss') })
    } else {
      const resolved = gameReducer(baseState, {
        type: 'match/resolved', outcome, goals: summary.playerGoals, assists: summary.playerAssists,
        appearances: isMatchBlock ? summary.blockAppearances : 1,
        minutesPlayed: isMatchBlock ? summary.blockMinutes?.reduce((total, minutes) => total + minutes, 0) : summary.playerMinutes ?? 90,
        wins: isMatchBlock ? summary.blockPlayerWins ?? summary.blockWins : undefined,
        streakOutcomes: isMatchBlock ? summary.blockOutcomes : undefined,
        opponents: summary.blockOpponents, ovrGain: isMatchBlock ? ovrGain : undefined, reputationGain,
      })
      if (isWorldCupElimination(baseState, outcome, summary.blockWins)) {
        const phase = isWorldCupGroup
          ? 'la fase de grupos'
          : baseState.week === 2 ? 'los dieciseisavos' : 'los octavos de final'
        const eliminated = gameReducer(resolved, { type: 'run/finished', message: `La selección queda eliminada en ${phase}.` })
        dispatch({ type: 'run/replaced', state: eliminated })
        setMatchRun(null)
        return
      }
      const completed = gameReducer(resolved, { type: 'node/completed', message: outcomeText })
      dispatch({ type: 'run/replaced', state: completed })
      setMatchRun(null)
    }
  }

  const openMatchReaction = (summary: MatchSummary, minute: number) => new Promise<boolean>((resume) => {
    if (!state || !matchRun) return
    const reactionEncounter = matchRun.encounter === 'derby' ? 'derby' : 'boss'
    const reactionProbability = (resource: 'card' | 'physical' | 'morale', cardId: CardRewardId | null = null) => bossReactionOdds(state, resource, cardId, reactionEncounter)
    const probabilityEffects = (resource: 'card' | 'physical' | 'morale', cardId: CardRewardId | null = null): DecisionChoice['effects'] => {
      const { win, loss } = reactionProbability(resource, cardId)
      return [
        { metric: 'condition', label: 'Victoria', value: `${win}%`, tone: 'gain', when: 'Remontas y ganas por un gol' },
        { metric: 'condition', label: 'Derrota', value: `${loss}%`, tone: 'cost', when: 'El rival conserva la ventaja' },
      ]
    }
    const reactionCards = REWARD_IDS.filter((id): id is CardRewardId => REWARDS[id].kind === 'card' && canUseCard(state.inventory, id as CardRewardId, 'boss'))
    const settle = (choiceId: string, reactedState: GameStateV2, resourceLabel: string, outcomeIndex?: number) => {
      const resolvedEffect = choices.find((choice) => choice.id === choiceId)?.effects[outcomeIndex ?? -1]
      const advanced = resolvedEffect?.metric === 'condition' && resolvedEffect.label === 'Victoria'
      const score = `${summary.homeGoals}-${summary.awayGoals}`
      const note = advanced
        ? `Reaccionas al minuto ${minute} con ${score}, remontas y terminas ganando por un gol usando ${resourceLabel}.`
        : `Reaccionas al minuto ${minute} con ${score} usando ${resourceLabel}, pero el rival conserva la ventaja.`
      bossReactionResultRef.current = { state: reactedState, note }
      resume(advanced)
    }
    const choices: DecisionChoice[] = [
      ...reactionCards.map((id): DecisionChoice => ({
        id: `boss-reaction-card:${id}`, title: `Jugar ${REWARDS[id].name}`, summary: 'Gastas el consumible para cambiar el partido antes de los penaltis.', art: REWARDS[id].art, badge: 'Reacción · Consumible', risk: 'low',
        effects: [{ metric: 'persistent', label: 'Coste', value: 'Consume ×1', tone: 'cost' }, ...probabilityEffects('card', id)],
      })),
      { id: 'boss-reaction-physical', title: 'Vaciar el depósito', summary: 'Aprietas desde el descanso para buscar una remontada antes del final.', art: 'event-training', badge: 'Reacción · Físico', risk: 'medium', effects: probabilityEffects('physical') },
      { id: 'boss-reaction-morale', title: 'Asumir el lanzamiento', summary: 'Tomas el mando desde el descanso para sostener la remontada.', art: 'event-press', badge: 'Reacción · Moral', risk: 'high', effects: probabilityEffects('morale') },
      { id: 'boss-reaction-accept', title: 'Aceptar el resultado', summary: 'No comprometes más recursos y das la ronda por perdida.', art: 'event-coach', badge: 'Decisión', risk: 'high', effects: [{ metric: 'condition', label: 'Resultado', value: 'Derrota', tone: 'cost' }] },
    ]
    const cardActions = Object.fromEntries(reactionCards.map((id) => {
      const choiceId = `boss-reaction-card:${id}`
      return [choiceId, (outcomeIndex?: number) => settle(choiceId, gameReducer(state, { type: 'boss/reactionUsed', resource: 'card', rewardId: id }), REWARDS[id].name, outcomeIndex)]
    }))
    openDecision({ eyebrow: `Minuto ${minute} · reacción`, title: 'El partido sigue vivo', description: 'Vas un gol abajo. Gasta un recurso para intentar remontar antes del final.', icon: 'mdi:soccer-field', choices }, {
      ...cardActions,
      'boss-reaction-physical': (outcomeIndex) => settle('boss-reaction-physical', gameReducer(state, { type: 'boss/reactionUsed', resource: 'physical' }), 'tu esfuerzo', outcomeIndex),
      'boss-reaction-morale': (outcomeIndex) => settle('boss-reaction-morale', gameReducer(state, { type: 'boss/reactionUsed', resource: 'morale' }), 'tu confianza', outcomeIndex),
      'boss-reaction-accept': () => {
        bossReactionResultRef.current = { state, note: `Aceptas el ${summary.homeGoals}-${summary.awayGoals} en el minuto ${minute} y el rival conserva la ventaja.` }
        resume(false)
      },
    })
  })

  const completeMatch = (summary: MatchSummary) => {
    if (!state || !matchRun) return
    const reaction = bossReactionResultRef.current
    if (reaction) {
      bossReactionResultRef.current = null
      finalizeMatch(summary, reaction.state, summary.outcome, reaction.note)
      return
    }
    finalizeMatch(summary, state)
  }

  function reset() {
    clearGame(localStorage)
    dispatch({ type: 'run/cleared' })
    setDialog(null); setPrepEncounter(null); setMatchRun(null); setBossSeriesResult(null); setNotice(null)
  }

  const signOut = async () => {
    if (!supabase) return
    if (user) await saveCloudProgress(user.id, state, achievementProfile)
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    clearGame(localStorage)
    localStorage.removeItem(ACHIEVEMENT_STORAGE_KEY)
    dispatch({ type: 'run/cleared' })
    setAchievementProfile(emptyAchievementProfile())
    setHydratedUserId(null)
    setSyncStatus('local')
    setDialog(null); setPrepEncounter(null); setMatchRun(null); setBossSeriesResult(null); setNotice(null)
  }

  const cycleTheme = () => setTheme(theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto')
  const themeLabel = { auto: 'Auto', light: 'Claro', dark: 'Oscuro' }[theme]
  const leagueRivals = useMemo(() => state
    ? getLeagueRivals(state.club, state.leagueMovements)
    : [], [state?.club, state?.leagueOpponentsPlayed, state?.leagueMovements])
  const leagueBlockFixtureCount = state ? getLeagueBlockFixtureCount(leagueRivals.length, state.week) : 0
  const internationalRivals = useMemo(() => state ? getInternationalRivals(state.player.country) : [], [state?.player.country])
  const worldCupGroupRivals = useMemo(() => state ? getWorldCup2026GroupRivals(state.player.country) : [], [state?.player.country])
  const isInternationalFixture = Boolean(state?.season === 5 && state.finaleKind === 'world-cup' && matchRun
    && (matchRun.encounter === 'boss' || (matchRun.encounter === 'match' && state.map[state.week]?.some((node) => node.type === 'match') === true)))
  const isWorldCupGroup = Boolean(isInternationalFixture && state?.week === 0 && matchRun?.encounter === 'match')
  const worldCupPhase = getWorldCup2026RoutePhaseTitle(state?.player.country ?? '', state?.week ?? 0)
  const syncLabel = !authReady ? 'Comprobando cuenta…'
    : user ? syncStatus === 'loading' ? 'Cargando desde la nube…'
      : syncStatus === 'saving' ? 'Sincronizando…'
        : syncStatus === 'synced' ? 'Guardado en la nube'
          : syncStatus === 'error' ? 'Sin conexión · guardado local'
            : 'Guardado local'
            : 'Guardado local'

  if (isBooting || waitingForAccountProgress) return <AppLoadingScreen />

  return (
    <>
      <a className="skip-link" href="#main-content">Saltar al contenido</a>
      <main id="main-content" className="app-shell">
        <header className={`topbar${state ? ' topbar--run' : ''}`}>
          <div className="brand"><img className="brand__crest" src="/assets/brand/roguelikefc-mark.svg" alt="" aria-hidden="true" /><span className="brand__copy"><strong>RoguelikeFC</strong><small>Carrera de leyenda</small></span></div>
          <TopbarActions
            running={Boolean(state)}
            syncStatus={syncStatus}
            syncLabel={syncLabel}
            buildAction={state ? <SidebarMenuButton open={sidebarOpen} onOpenChange={setSidebarOpen} /> : undefined}
            account={{ label: user ? 'Cuenta' : 'Entrar', ariaLabel: user ? `Cuenta de ${user.email}` : 'Iniciar sesión o crear una cuenta', icon: user ? 'mdi:account-check-outline' : 'mdi:account-outline' }}
            theme={{ label: themeLabel, icon: theme === 'light' ? 'mdi:white-balance-sunny' : theme === 'dark' ? 'mdi:weather-night' : 'mdi:theme-light-dark' }}
            onAccount={() => setShowAccount(true)}
            onTheme={cycleTheme}
            onAchievements={() => setShowAchievements(true)}
            onGlossary={() => setShowGlossary(true)}
            onChangelog={() => setShowChangelog(true)}
            onRestart={() => setNotice({ title: '¿Reiniciar la run?', text: 'Perderás el progreso de esta carrera en este dispositivo y en la nube. Tus logros desbloqueados se conservarán.', label: 'Sí, reiniciar', action: reset, secondaryLabel: 'Cancelar', secondaryAction: () => setNotice(null) })}
          />
        </header>

        {!state ? <SetupScreen onStart={start} /> : <div className="game-grid"><SeasonMap state={state} onNode={openNode} onBoss={startBoss} /><Sidebar state={state} open={sidebarOpen} onOpenChange={setSidebarOpen} /></div>}
      </main>

      <AchievementDialog open={showAchievements} state={state} profile={achievementProfile} onClose={() => setShowAchievements(false)} />
      <FirstVisitTutorial open={shouldShowFirstVisitTutorial} onComplete={() => setShowFirstVisitTutorial(false)} />
      <AccountDialog open={showAccount} user={user} authReady={authReady} passwordRecovery={passwordRecovery} syncLabel={syncLabel} onClose={() => setShowAccount(false)} onSignOut={signOut} onPasswordRecoveryComplete={completePasswordRecovery} />
      {showGlossary && <Suspense fallback={<div className="dialog-backdrop" role="status" aria-live="polite"><div className="notice-dialog"><p className="eyebrow">Manual de la build</p><p>Abriendo glosario…</p></div></div>}><GlossaryDialog open discoveredRewards={achievementProfile.discoveredRewards} onClose={() => setShowGlossary(false)} /></Suspense>}
      <ChangelogDialog open={showChangelog} onClose={() => setShowChangelog(false)} />
      {state && prepEncounter && <MatchPrepDialog state={state} encounter={prepEncounter.kind} opponent={prepEncounter.opponent} onConfirm={prepareMatch} />}
      {state && matchRun && state.pendingMatch && <div className="dialog-backdrop"><section ref={matchResultDialogRef} className="decision-dialog match-result-dialog" role="dialog" aria-modal="true" aria-label="Relato del partido" tabIndex={-1}><MatchTicker options={{ matchId: matchRun.id, title: matchRun.encounter === 'boss' ? getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind).rounds[state.boss?.round ?? 0] ?? getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind).name : matchRun.encounter === 'derby' ? 'El derbi' : isInternationalFixture ? worldCupPhase : 'Tramo de liga', homeTeam: isInternationalFixture ? state.player.country : state.club, opponent: matchRun.opponent, format: isWorldCupGroup ? 'world-cup-group' : matchRun.encounter === 'match' && !isInternationalFixture ? 'league-block' : 'single', opponents: isWorldCupGroup ? worldCupGroupRivals.length ? worldCupGroupRivals : internationalRivals.slice(0, 3) : matchRun.encounter === 'match' && !isInternationalFixture ? leagueRivals.map((club) => club.name) : undefined, previousOpponents: matchRun.encounter === 'match' && !isInternationalFixture ? state.leagueOpponentsPlayed : undefined, fixtureCount: isWorldCupGroup ? 3 : matchRun.encounter === 'match' && !isInternationalFixture ? leagueBlockFixtureCount : undefined, clubReputation: isWorldCupGroup ? 100 : state.clubReputation, careerStage: state.season, playerOverall: state.ovr, intensity: matchRun.encounter === 'match' ? 'normal' : matchRun.encounter, expectedResult: matchRun.outcome, winProbability: matchRun.probability, player: state.player, assistBonusPercentagePoints: state.pendingMatch.assistChanceBonus * 100 }} onComplete={completeMatch} onMatchReaction={matchRun.encounter === 'boss' || matchRun.encounter === 'derby' ? openMatchReaction : undefined} /></section></div>}
      <DecisionDialog dialog={dialog} onChoose={resolveChoice} />
      {bossSeriesResult && <BossSeriesResultDialog state={bossSeriesResult} onContinue={continueAfterBossSeries} />}
      {notice && <div className="dialog-backdrop"><section ref={noticeDialogRef} className="notice-dialog" role="dialog" aria-modal="true" aria-labelledby="notice-title" tabIndex={-1}><p className="eyebrow">Informe de carrera</p><h2 id="notice-title">{notice.title}</h2><p>{notice.text}</p><div><button className="button button--primary" type="button" onClick={notice.action}>{notice.label}</button>{notice.secondaryLabel && <button className="button notice-dialog__secondary" data-modal-cancel type="button" onClick={notice.secondaryAction}>{notice.secondaryLabel}</button>}</div></section></div>}
      {state?.finished && <CareerReportDialog state={state} onRestart={reset} />}
    </>
  )
}
